import React, { useEffect, useMemo, useRef, useState } from "react";
import { Phone, Copy, Check, Users } from "lucide-react";
import { UNITS, EVENTS_WHATSAPP, EVENTS_PHONE, EVENTS_INSTAGRAM, nairaEvents, thumbFor } from "../data/cateringMenu";
import { ALLERGEN_CHIPS, ALLERGY_DISCLAIMER } from "../lib/allergy";
import EventGallery from "../components/EventGallery";
import api from "../lib/api";
import "./events.css";

const WhatsAppGlyph = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z" />
  </svg>
);

const FACTS = [
  { n: "12", title: "Servings per dish", body: "Unless a dish is marked otherwise, each price covers 12 pieces or portions. Need a few more? Add individual extra pieces on top of any full tray." },
  { n: "20%", title: "Service charge", body: "Added to your food total. Covers food styling, one service personnel, on-site service and supervision, and crockery." },
  { n: "+", title: "Logistics fee", body: "Compulsory and quoted separately, based on your event location. Tables are not included in our setup." },
  { n: "4h", title: "Service window", body: "Service runs for 4 hours from the agreed call time. Extensions are \u20a620,000 per hour. Events past 11:00pm carry an extra charge." },
];

const TERMS = [
  { label: "Portions & Pricing", points: [
    "Each item on the menu is priced to include a serving of 12 pieces, unless the dish is marked as a single portion, glass, slice or board.",
    "Additional pieces can be added on top of any full tray, priced per piece.",
    "A 20% service charge applies to the total cost. It includes food styling, 1 service personnel, on-site service and supervision, and crockery.",
  ] },
  { label: "Setup & Service", points: [
    "Tables are not provided as part of our setup services.",
    "This spread is self-service. Servers are available on request at an extra cost.",
    "A compulsory logistics fee applies and is determined by the event location.",
  ] },
  { label: "Payment", points: [
    "Full payment, or a deposit of 80%, is required to secure a booking.",
    "The balance must be paid in full before the day of the event.",
  ] },
  { label: "Timing", points: [
    "Service time is limited to 4 hours after the agreed call time.",
    "An extension requested by the client incurs an additional fee of \u20a620,000 per hour.",
    "An extra charge applies to events that run beyond 11:00pm.",
  ] },
];

// guests -> {trays, extras}
const guestSplit = (g) => {
  const guests = parseInt(g) || 0;
  const trays = Math.max(1, Math.floor(guests / 12));
  const extras = guests > 12 ? guests % 12 : 0;
  return { trays, extras };
};

export default function EventsCatering() {
  const [menu, setMenu] = useState([]);
  const [presets, setPresets] = useState([]);
  const [qty, setQty] = useState({});      // trays / units, keyed by `${catId}-${idx}`
  const [extras, setExtras] = useState({}); // extra pieces (TRAY only)
  const [guests, setGuests] = useState(60);
  const [activeCat, setActiveCat] = useState("");
  const [showEmpty, setShowEmpty] = useState(false);
  const [copied, setCopied] = useState(false);
  const [convertPrompt, setConvertPrompt] = useState(null);
  const [allergyChoice, setAllergyChoice] = useState(null);
  const [allergyTags, setAllergyTags] = useState([]);
  const [allergyNote, setAllergyNote] = useState("");
  const sectionRefs = useRef({});
  const tabClickRef = useRef(false);

  useEffect(() => {
    api.get("/events/menu").then(({ data }) => {
      setMenu(data);
      if (data[0]) setActiveCat(data[0].id);
    }).catch(() => {});
    api.get("/events/packages").then(({ data }) => setPresets(data)).catch(() => {});
  }, []);

  const keyFor = (catId, idx) => `${catId}-${idx}`;

  const nameToKey = useMemo(() => {
    const map = {};
    menu.forEach((cat) => cat.items.forEach((item, idx) => { map[`${cat.id}|${item.name}`] = keyFor(cat.id, idx); }));
    return map;
  }, [menu]);

  const itemByKey = useMemo(() => {
    const map = {};
    menu.forEach((cat) => cat.items.forEach((item, idx) => { map[keyFor(cat.id, idx)] = { ...item, catId: cat.id }; }));
    return map;
  }, [menu]);

  const setItemQty = (k, delta) =>
    setQty((prev) => {
      const next = Math.max(0, (prev[k] || 0) + delta);
      const copy = { ...prev };
      if (next === 0) { delete copy[k]; setExtras((e) => { const c = { ...e }; delete c[k]; return c; }); }
      else copy[k] = next;
      return copy;
    });

  const setItemExtras = (k, delta) => {
    if (delta > 0 && (extras[k] || 0) >= 11) { setConvertPrompt(k); return; }
    setConvertPrompt(null);
    setExtras((prev) => {
      const next = Math.max(0, Math.min(11, (prev[k] || 0) + delta));
      const copy = { ...prev };
      if (next === 0) delete copy[k]; else copy[k] = next;
      return copy;
    });
  };

  const convertToTray = (k) => {
    setItemQty(k, 1);
    setExtras((prev) => { const c = { ...prev }; delete c[k]; return c; });
    setConvertPrompt(null);
  };

  const matchRowToGuests = (k) => {
    const { trays, extras: ex } = guestSplit(guests);
    setQty((prev) => ({ ...prev, [k]: trays }));
    setExtras((prev) => { const c = { ...prev }; if (ex > 0) c[k] = ex; else delete c[k]; return c; });
  };

  const applyGuestsToAll = () => {
    const { trays, extras: ex } = guestSplit(guests);
    setQty((prev) => {
      const copy = { ...prev };
      Object.keys(prev).forEach((k) => { if (itemByKey[k]?.unit === "TRAY") copy[k] = trays; });
      return copy;
    });
    setExtras((prev) => {
      const copy = { ...prev };
      Object.keys(qty).forEach((k) => { if (itemByKey[k]?.unit === "TRAY") { if (ex > 0) copy[k] = ex; else delete copy[k]; } });
      return copy;
    });
  };

  const addByRef = (cat, name) => {
    const k = nameToKey[`${cat}|${name}`];
    if (k) setItemQty(k, 1);
  };

  const applyPreset = (preset) => {
    const next = {};
    preset.items.forEach(({ cat, name, qty: n }) => {
      const k = nameToKey[`${cat}|${name}`];
      if (k) next[k] = n;
    });
    setQty(next);
    setExtras({});
    setGuests(preset.serves);
    setTimeout(() => document.querySelector("[data-testid='ev-estimate-panel']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const extraPriceOf = (item) => (item.extra_piece_price != null ? item.extra_piece_price : (item.unit === "TRAY" ? Math.round(item.price / 12) : 0));

  const selectedLines = useMemo(() => {
    const lines = [];
    menu.forEach((cat) => {
      cat.items.forEach((item, idx) => {
        const k = keyFor(cat.id, idx);
        const t = qty[k] || 0;
        const ex = extras[k] || 0;
        if (t > 0 || ex > 0) {
          const ep = extraPriceOf(item);
          const total = t * item.price + ex * ep;
          lines.push({ k, name: item.name, unit: item.unit, isTray: item.unit === "TRAY", trays: t, extras: ex, extraPrice: ep, price: item.price, total });
        }
      });
    });
    return lines;
  }, [qty, extras, menu]);

  const subtotal = selectedLines.reduce((s, l) => s + l.total, 0);
  const service = Math.round(subtotal * 0.2);
  const total = Math.round(subtotal * 1.2);
  const hasSelection = selectedLines.length > 0;
  const gs = guestSplit(guests);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (tabClickRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveCat(visible[0].target.id.replace("cat-", ""));
      },
      { rootMargin: "-160px 0px -55% 0px", threshold: [0, 0.25, 0.5] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [menu]);

  const scrollToCat = (id) => {
    setActiveCat(id);
    tabClickRef.current = true;
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => { tabClickRef.current = false; }, 800);
  };

  const lineLabelWA = (l) => {
    if (l.isTray) {
      const trayPart = `${l.trays} tray${l.trays === 1 ? "" : "s"} ${l.name}`;
      const exPart = l.extras > 0 ? ` + ${l.extras} extra piece${l.extras === 1 ? "" : "s"}` : "";
      return `${trayPart}${exPart} \u2014 ${nairaEvents(l.total)}`;
    }
    return `${l.trays} \u00d7 ${l.name} (${UNITS[l.unit].label}) \u2014 ${nairaEvents(l.total)}`;
  };

  const buildMessage = () => {
    const lines = selectedLines.map(lineLabelWA).join("\n");
    return `Sapore Events & Catering enquiry
Guests: ${parseInt(guests) || 0}
ALLERGIES: ${allergyChoice === "yes" ? ([allergyTags.join(", "), allergyNote.trim()].filter(Boolean).join(" — ") || "Yes") : "None noted"}

${lines}

Food subtotal: ${nairaEvents(subtotal)}
Service charge (20%): ${nairaEvents(service)}
Estimated total: ${nairaEvents(total)}
(Excludes logistics fee, tables, servers and any extensions.)`;
  };

  const sendWhatsApp = () => {
    if (!hasSelection) { setShowEmpty(true); return; }
    window.open(`https://wa.me/${EVENTS_WHATSAPP}?text=${encodeURIComponent(buildMessage())}`, "_blank", "noopener");
  };

  const copySelection = async () => {
    if (!hasSelection) { setShowEmpty(true); return; }
    try {
      await navigator.clipboard.writeText(buildMessage());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setCopied(false); }
  };

  useEffect(() => { if (hasSelection) setShowEmpty(false); }, [hasSelection]);

  if (!menu.length) {
    return (
      <div className="sapore-events" data-testid="ev-loading" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-soft)" }}>
        Loading catering menu…
      </div>
    );
  }

  return (
    <div className={`sapore-events${hasSelection ? " has-bar" : ""}`}>
      {/* 1. HERO */}
      <section className="ev-hero">
        <div className="ev-hero-inner">
          <p className="ev-eyebrow">Sapore Eats · Private &amp; Corporate</p>
          <h1>Events &amp; Catering</h1>
          <p className="ev-lede">
            Styled food displays for weddings, corporate breakfasts, birthdays and private gatherings across Lagos. Every
            dish below is priced as a full tray — and you can top up with individual extra pieces to match your headcount.
          </p>
          <div className="ev-hero-actions">
            <a href="#menu" className="ev-btn ev-btn-light" data-testid="hero-browse-menu">Browse the catering menu</a>
            <a href="#terms" className="ev-btn ev-btn-ghost-light" data-testid="hero-how-it-works">How it works</a>
          </div>
          <p className="ev-sep-note">
            Looking for our daily breakfast &amp; fresh-pressed juices? That's a separate service — order breakfast on the{" "}
            <a href="/">home page</a>. This page covers event catering only.
          </p>
        </div>
      </section>

      {/* 2. FACTS */}
      <section className="ev-section">
        <p className="ev-section-eyebrow">Before you build your spread</p>
        <h2>The essentials, up front</h2>
        <div className="ev-facts">
          {FACTS.map((f) => (
            <div className="ev-fact" key={f.title}>
              <div className="ev-numeral ev-num">{f.n}</div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Package presets */}
      <section className="ev-section" style={{ paddingTop: 0 }}>
        <p className="ev-section-eyebrow">Start with a package</p>
        <h2>One-tap starting points</h2>
        <p className="ev-cat-note" style={{ maxWidth: 640 }}>Pick a ready-made spread to fill your estimate instantly, then fine-tune quantities to match your headcount.</p>
        <div className="ev-presets">
          {presets.map((p) => (
            <button key={p.id} className="ev-preset-card" onClick={() => applyPreset(p)} data-testid={`ev-preset-${p.id}`}>
              <span className="ev-preset-serves">Serves ~{p.serves}</span>
              <span className="ev-preset-title">{p.title}</span>
              <span className="ev-preset-blurb">{p.blurb}</span>
              <span className="ev-preset-cta">Use this package &rarr;</span>
            </button>
          ))}
        </div>
      </section>

      {/* 3 + 4. MENU + ESTIMATE */}
      <section className="ev-section" id="menu" style={{ paddingTop: 0 }}>
        <div className="ev-menu-layout">
          <div className="ev-menu-col">
            <div className="ev-tabs" role="tablist" aria-label="Catering categories">
              {menu.map((cat) => (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={activeCat === cat.id}
                  className="ev-tab"
                  onClick={() => scrollToCat(cat.id)}
                  data-testid={`ev-tab-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {menu.map((cat) => (
              <div
                key={cat.id}
                id={`cat-${cat.id}`}
                className="ev-cat"
                ref={(el) => (sectionRefs.current[cat.id] = el)}
                data-testid={`ev-cat-${cat.id}`}
              >
                <div className="ev-cat-head">
                  <h3>{cat.name}</h3>
                  <span className="ev-unit-label">{cat.unitLabel}</span>
                </div>
                <p className="ev-cat-note">{cat.note}</p>
                {cat.items.map((item, idx) => {
                  const k = keyFor(cat.id, idx);
                  const t = qty[k] || 0;
                  const ex = extras[k] || 0;
                  const unit = UNITS[item.unit];
                  const isTray = item.unit === "TRAY";
                  const ep = extraPriceOf(item);
                  const thumb = item.image_public_id ? null : thumbFor(cat.id, idx);
                  return (
                    <div className="ev-item" key={k} data-testid={`ev-item-${k}`}>
                      {thumb && <img className="ev-thumb" src={thumb} alt="" loading="lazy" />}
                      <div className="ev-item-name">{item.name}</div>
                      <div className="ev-item-meta">
                        <span className="ev-pill">{unit.label}</span>
                        {isTray && (
                          <span className="ev-perpiece">
                            &asymp; {nairaEvents(ep)} per piece
                          </span>
                        )}
                        {item.note && <span>· {item.note}</span>}
                      </div>
                      <div className="ev-item-price ev-num">{nairaEvents(item.price)}</div>
                      <div className="ev-stepper-wrap">
                        <div className="ev-stepper">
                          <button className="ev-step-btn" aria-label={`Remove one ${item.name}`} onClick={() => setItemQty(k, -1)} data-testid={`ev-minus-${k}`}>&minus;</button>
                          <span className="ev-step-count ev-num" data-zero={t === 0} data-testid={`ev-qty-${k}`}>{t}</span>
                          <button className="ev-step-btn" aria-label={`Add one ${item.name}`} onClick={() => setItemQty(k, 1)} data-testid={`ev-plus-${k}`}>+</button>
                          <span className="ev-stepper-label">{isTray ? (t === 1 ? "tray" : "trays") : ""}</span>
                        </div>
                      </div>

                      {isTray && (item.allows_extras !== false) && (
                        <div className="ev-extras-row" data-testid={`ev-extras-row-${k}`}>
                          <div className="ev-stepper ev-stepper-sm">
                            <button className="ev-step-btn" aria-label={`Remove one extra piece of ${item.name}`} disabled={t < 1} onClick={() => setItemExtras(k, -1)} data-testid={`ev-extra-minus-${k}`}>&minus;</button>
                            <span className="ev-step-count ev-num" data-zero={ex === 0} data-testid={`ev-extra-qty-${k}`}>{ex}</span>
                            <button className="ev-step-btn" aria-label={`Add one extra piece of ${item.name}`} disabled={t < 1} onClick={() => setItemExtras(k, 1)} data-testid={`ev-extra-plus-${k}`}>+</button>
                          </div>
                          <span className="ev-extras-note">{t < 1 ? "Add a tray first to top up with extra pieces" : `extra pieces · ${nairaEvents(ep)} each`}</span>
                          {t > 0 && (
                            <button className="ev-pair-chip" onClick={() => matchRowToGuests(k)} data-testid={`ev-match-${k}`}>
                              <Users size={12} style={{ marginRight: 4 }} />Match to {parseInt(guests) || 0} guests
                            </button>
                          )}
                        </div>
                      )}

                      {convertPrompt === k && (
                        <div className="ev-pairs" data-testid={`ev-convert-${k}`}>
                          <span className="ev-pairs-label">That's a full tray —</span>
                          <button className="ev-pair-chip" onClick={() => convertToTray(k)} data-testid={`ev-convert-btn-${k}`}>add one tray instead</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Estimate panel */}
          <div className="ev-estimate-col">
            <div className="ev-estimate" data-testid="ev-estimate-panel">
              <h3>Your estimate</h3>
              <p className="ev-estimate-sub">Indicative only — final quote confirmed by Sapore.</p>

              <label className="ev-guest-label" htmlFor="ev-guests">Guest count</label>
              <input
                id="ev-guests"
                className="ev-guest-input"
                type="number"
                inputMode="numeric"
                min="1"
                value={guests}
                onChange={(e) => setGuests(e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="ev-guest-input"
              />
              <p className="ev-guest-hint">
                For {parseInt(guests) || 0} guests, that's about <strong>{gs.trays} {gs.trays === 1 ? "tray" : "trays"}{gs.extras > 0 ? ` plus ${gs.extras} extra piece${gs.extras === 1 ? "" : "s"}` : ""}</strong> of any dish you want everyone to have. Grazing items can go lighter.
              </p>
              {hasSelection && (
                <button className="ev-btn ev-btn-outline" style={{ width: "100%", marginBottom: 12 }} onClick={applyGuestsToAll} data-testid="ev-apply-all">
                  <Users size={15} /> Match all trays to guest count
                </button>
              )}

              {hasSelection ? (
                <ul className="ev-lines" data-testid="ev-estimate-lines">
                  {selectedLines.map((l) => (
                    <li className="ev-line" key={l.k}>
                      <span>
                        {l.trays} &times; {l.name}
                        {l.isTray && l.extras > 0 ? ` + ${l.extras} extra` : ""}
                      </span>
                      <span className="ev-line-total ev-num">{nairaEvents(l.total)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="ev-empty-lines">No dishes selected yet — add something from the menu.</p>
              )}

              <div className="ev-totals">
                <div className="ev-total-row"><span>Food subtotal</span><span className="ev-num" data-testid="ev-subtotal">{nairaEvents(subtotal)}</span></div>
                <div className="ev-total-row"><span>Service charge (20%)</span><span className="ev-num" data-testid="ev-service">{nairaEvents(service)}</span></div>
                <div className="ev-total-row"><span>Logistics fee</span><span className="ev-total-quoted">Quoted by location</span></div>
                <div className="ev-total-row grand"><span>Estimated total</span><span className="ev-num" data-testid="ev-total">{nairaEvents(total)}</span></div>
              </div>

              <div className="ev-exclusions">
                Not included: logistics fee, tables, servers (this spread is self-service — servers available on request),
                service extensions past 4 hours, and after-11:00pm charges.
              </div>

              <div className="ev-exclusions" style={{ background: "var(--surface)", border: "1px solid var(--line)" }} data-testid="ev-allergy-block">
                <p style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>Does anyone eating this have a food allergy?</p>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {[["no", "No allergies"], ["yes", "Yes"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`ev-allergy-${v}`} onClick={() => setAllergyChoice(v)} className="ev-tab" aria-selected={allergyChoice === v} style={{ flex: 1 }}>{l}</button>
                  ))}
                </div>
                {allergyChoice === "yes" && (
                  <div data-testid="ev-allergy-details">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                      {ALLERGEN_CHIPS.map((chip) => (
                        <button key={chip} type="button" className="ev-pair-chip" onClick={() => setAllergyTags((prev) => prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip])} style={allergyTags.includes(chip) ? { background: "var(--orchid-soft)" } : {}}>{chip}</button>
                      ))}
                    </div>
                    <textarea data-testid="ev-allergy-note" value={allergyNote} onChange={(e) => setAllergyNote(e.target.value.slice(0, 300))} rows={2} placeholder="Anything else we should know" style={{ width: "100%", borderRadius: 10, border: "1px solid var(--line)", padding: 10, fontFamily: "inherit", fontSize: 13 }} />
                  </div>
                )}
                <p style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 8 }}>{ALLERGY_DISCLAIMER}</p>
              </div>

              <div className="ev-estimate-actions">
                <button className="ev-btn ev-btn-primary" onClick={sendWhatsApp} data-testid="ev-send-whatsapp">
                  <WhatsAppGlyph /> Send this to Sapore
                </button>
                <button className="ev-btn ev-btn-outline" onClick={copySelection} data-testid="ev-copy-selection">
                  {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy my selection</>}
                </button>
                {showEmpty && <p className="ev-inline-msg" data-testid="ev-empty-msg">Add a dish first.</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <EventGallery />

      {/* 5. TERMS */}
      <div className="ev-terms-band" id="terms">
        <section className="ev-section">
          <p className="ev-section-eyebrow">How it works</p>
          <h2>Terms &amp; conditions</h2>
          <div className="ev-terms-grid">
            {TERMS.map((t) => (
              <div className="ev-term-card" key={t.label}>
                <div className="ev-term-label">{t.label}</div>
                <ul>{t.points.map((p) => <li key={p}>{p}</li>)}</ul>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 6. CLOSING CTA */}
      <section className="ev-cta">
        <h2>Let's plan your spread</h2>
        <p>Send us your date, guest count and location and we'll come back with a full quote including logistics.</p>
        <div className="ev-cta-actions">
          <a className="ev-btn ev-btn-light" href={`https://wa.me/${EVENTS_WHATSAPP}`} target="_blank" rel="noreferrer" data-testid="ev-cta-whatsapp">
            <WhatsAppGlyph /> Chat on WhatsApp
          </a>
          <a className="ev-btn ev-btn-ghost-light" href={`tel:${EVENTS_PHONE}`} data-testid="ev-cta-call">
            <Phone size={16} /> Call {EVENTS_PHONE}
          </a>
        </div>
        <p className="ev-cta-ig">Instagram <a href={EVENTS_INSTAGRAM} target="_blank" rel="noreferrer">@Sapore_eats</a></p>
      </section>

      {/* 7. FOOTNOTE */}
      <p className="ev-footnote">
        Prices are in Nigerian Naira and subject to change. The estimate tool on this page is a planning aid, not a binding quote.
      </p>

      {/* Mobile fixed bottom bar */}
      {hasSelection && (
        <div className="ev-bottom-bar" data-testid="ev-bottom-bar">
          <div>
            <span className="ev-bb-label">Estimated total</span>
            <span className="ev-bb-total ev-num">{nairaEvents(total)}</span>
          </div>
          <button className="ev-btn ev-btn-light" onClick={sendWhatsApp} data-testid="ev-bottom-send">
            <WhatsAppGlyph /> Send to Sapore
          </button>
        </div>
      )}
    </div>
  );
}
